import { useMemo } from 'react';
import { SharedSidebarShell, resolveWorkspaceNav } from '@zafroi/ui-kit';
import type { SidebarNavGroup } from '@zafroi/ui-kit';
import { Logo } from './Logo';
import {
  filterNavForUser,
  navGroupLabels,
  navItems,
  type NavGroup,
  type NavItem,
} from '@/config/navigation';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * Ops sidebar — renders through `@zafroi/ui-kit`'s `SharedSidebarShell`
 * so the chrome is byte-identical to engage.zafroi.com (same component).
 * The Ops-specific concerns (nav config, role-based filtering, group
 * sectioning, ERP-flavoured Logo) sit on top.
 *
 * The kit's nav shape uses string-keyed groups, plain hrefs, and
 * optional badges. We transform Ops's NavItem[] (organised by `group`
 * tags) into the kit's SidebarNavGroup[] preserving Ops's section order
 * + the existing `navGroupLabels` display strings.
 */
export function Sidebar({ collapsed, onToggle }: Props) {
  const me = useCurrentUser();

  // ADR-041/051: when the tenant is on the unified workspace nav (COMMERCE once
  // activated, SOFTWARE today), the runtime serves ONE cross-app sidebar in
  // `workspace.nav`, already permission-filtered server-side. Operations renders
  // it — resolved against `workspace.apps` for cross-app hrefs — so the user
  // navigates every app from here. Absent → the local, feature-flag-gated Ops nav
  // exactly as before. This is the same swap intent/kosh/catalog/experience make.
  const unifiedNav = me?.workspace?.nav ?? null;
  const nav: SidebarNavGroup[] = useMemo(() => {
    if (unifiedNav && unifiedNav.length) {
      return resolveWorkspaceNav(unifiedNav, 'operations', me?.workspace?.apps);
    }
    const visible = filterNavForUser(navItems, me);
    return toSidebarNavGroups(visible);
  }, [me, unifiedNav]);

  return (
    <div className="hidden md:flex md:h-screen">
      <SharedSidebarShell
        nav={nav}
        collapsed={collapsed}
        onToggle={onToggle}
        header={
          <Logo
            variant={collapsed ? 'mark' : 'lockup'}
            tagline="ERP"
            glow
          />
        }
        footer={
          !collapsed ? (
            <div className="text-[10.5px] leading-snug text-sidebar-muted/80">
              Operational Intelligence
              <div className="text-sidebar-muted/60">v0.1 · MVP1</div>
            </div>
          ) : null
        }
      />
    </div>
  );
}

/**
 * Bucket the flat NavItem[] into the kit's SidebarNavGroup[]. Group order
 * is preserved as the order in which groups are FIRST encountered (matches
 * the order in `navItems`); group labels come from `navGroupLabels`.
 *
 * The kit's SharedSidebarShell renders a thin divider between adjacent
 * groups, so any empty-label group still gets visual separation without
 * redundant text — same behaviour as the previous custom sidebar.
 */
function toSidebarNavGroups(items: NavItem[]): SidebarNavGroup[] {
  const seen = new Set<NavGroup>();
  const order: NavGroup[] = [];
  for (const item of items) {
    if (!seen.has(item.group)) {
      seen.add(item.group);
      order.push(item.group);
    }
  }
  return order.map<SidebarNavGroup>((group) => ({
    id: group,
    label: navGroupLabels[group] || undefined,
    items: items
      .filter((i) => i.group === group)
      .map((i) => ({
        id: i.to,
        label: i.label,
        icon: i.icon,
        href: i.to,
        end: i.to === '/',
      })),
  }));
}
